import { useTranslation } from 'react-i18next'

import { isLanguage, LOCALE_TAGS, type Language } from '@/i18n/config'

/* The active UI language and its `Intl` tag, read from i18next alone.
 *
 * SESSION-FREE ON PURPOSE. `useLanguage()` follows the signed-in user's stored
 * preference, which makes it a `useSession` call — and a component that only
 * wants to print a date in the right language would then need a QueryClient to
 * render, and drag the session query into every one of its tests. That cost is
 * why a sheet and the chat panel had each gone round the old session-bound
 * `useLocaleTag` and printed English dates on a Russian screen.
 *
 * The session is still the authority: AppShell mounts `useLanguage()` once,
 * which writes the preference into i18next. By the time anything below it
 * renders, i18next holds the resolved language, and reading it here gives the
 * same answer without the dependency. `useTranslation` re-renders on
 * `languageChanged`, so a switch re-formats every date that reads this. */

/** The active UI language, as i18next resolved it. */
export function useActiveLanguage(): Language {
  const { i18n } = useTranslation()
  return isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en'
}

/** The BCP 47 tag for `Intl` formatting in the active language. */
export function useLocaleTag(): string {
  return LOCALE_TAGS[useActiveLanguage()]
}
